import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { transferToBranch } from "@/integrations/supabase/inventory-services";
import { supabase } from "@/integrations/supabase/client";
import { MainInventoryWithRelations } from "@/types/inventory";

interface TransferToBranchModalProps {
  isOpen: boolean;
  onClose: () => void;
  inventoryItem: MainInventoryWithRelations | null;
  onSuccess: () => void;
}

const TransferToBranchModal = ({ isOpen, onClose, inventoryItem, onSuccess }: TransferToBranchModalProps) => {
  const [branchId, setBranchId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const { toast } = useToast();

  const { data: branches } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('branches')
        .select('*')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  const transferMutation = useMutation({
    mutationFn: ({ sparePartTypeId, branchId, quantity, notes }: {
      sparePartTypeId: string;
      branchId: string;
      quantity: number;
      notes?: string;
    }) => transferToBranch(sparePartTypeId, branchId, quantity, notes),
    onSuccess: () => {
      toast({
        title: "¡Éxito!",
        description: "Transferencia realizada correctamente.",
      });
      onSuccess();
      handleClose();
    },
    onError: (error: unknown) => {
      console.error("Error transferring to branch:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al realizar la transferencia.";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!branchId || !quantity || !inventoryItem) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Por favor completa todos los campos requeridos.",
      });
      return;
    }

    const quantityNum = parseInt(quantity);
    if (quantityNum <= 0 || quantityNum > inventoryItem.quantity) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "La cantidad debe ser mayor a 0 y menor o igual al stock disponible.",
      });
      return;
    }

    transferMutation.mutate({
      sparePartTypeId: inventoryItem.spare_part_type_id,
      branchId,
      quantity: quantityNum,
      notes: notes.trim() || undefined
    });
  };

  const handleClose = () => {
    setBranchId("");
    setQuantity("");
    setNotes("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Transferir a Sucursal</DialogTitle>
        </DialogHeader>

        {inventoryItem && (
          <div className="mb-4 p-3 bg-gray-50 rounded-md">
            <p className="font-medium">{inventoryItem.spare_part_type?.name}</p>
            <p className="text-sm text-gray-600">
              Stock disponible: {inventoryItem.quantity} unidades
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="branch">Sucursal Destino</Label>
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona una sucursal" />
              </SelectTrigger>
              <SelectContent>
                {branches?.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="quantity">Cantidad a Transferir</Label>
            <Input
              id="quantity"
              type="number"
              min="1"
              max={inventoryItem?.quantity || 0}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Cantidad"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notas (Opcional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas sobre la transferencia..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={transferMutation.isPending}>
              {transferMutation.isPending ? "Transfiriendo..." : "Transferir"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TransferToBranchModal;
